import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import GridDisplay from '../../../../v2BaseComponents/GridDisplay';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import ReportCard from './ReportCard';
import { useReports } from '../../../../../hooks/api/useReports';
import { Reports } from '../../../../../primary-record-types';
import { reportTypes } from '../../../../../utils/reports';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { useIsLoadingPermissions } from '../../../../v2BaseComponents/Permissions/hooks';
import { getSpacing } from '../../../../../utils/spacing';
import { useRedirectToFeedbackAndRoadmap } from '../../../../../hooks/useRedirectToFeedbackAndRoadmap';

const intl = getIntl('components.screens.tabs.profiles.ReportsList', {
    feedback: 'Feedback',
});

export type ReportsListOwnProps = { profileId: string };

export type ReportsListProps = ReportsListOwnProps;

const ReportsList: React.FC<ReportsListProps> = (props) => {
    const { redirectToFeedbackAndRoadmap, isRedirecting } = useRedirectToFeedbackAndRoadmap();
    const recordEvent = useRecordEvent();
    const isMobile = useIsMobileLayout();
    const { generateReport, isGeneratingReport, reports, isLoadingReports, sharedReports, isLoadingSharedReports } =
        useReports({
            medicalProfileId: props.profileId,
            notifyOnProcessingComplete: true,
        });
    const isLoadingPermissions = useIsLoadingPermissions(props.profileId);
    const getReportRecord = (reportType: Reports.ReportType) =>
        reports.find((report) => report.reportType === reportType) || null;
    const getSharedReportRecord = (reportType: Reports.ReportType) =>
        sharedReports.find((report) => report.reportType === reportType) || null;

    return (
        <AnimateLoadedContent
            isLoading={isLoadingReports || isLoadingPermissions || isLoadingSharedReports || isRedirecting}
        >
            {() => (
                <ActionButtonLayout
                    actions={[
                        {
                            title: intl('feedback'),
                            onPress: redirectToFeedbackAndRoadmap,
                            icon: 'MessageSquare',
                        },
                    ]}
                    scrollable
                >
                    <MainContentContainer>
                        <GridDisplay
                            itemsPerRow={isMobile ? 1 : 2}
                            justifyRows="flex-start"
                            padding={getSpacing(4)}
                            items={reportTypes
                                .filter((_) => _ !== 'all-medications')
                                .map((reportType) => (
                                    <ReportCard
                                        key={reportType}
                                        profileId={props.profileId}
                                        report={getReportRecord(reportType)}
                                        reportType={reportType}
                                        onClick={() => {
                                            recordEvent('createReport', {
                                                reportType,
                                            });
                                            generateReport({
                                                request: {
                                                    type: reportType,
                                                },
                                            });
                                        }}
                                        isDisabled={isGeneratingReport}
                                        testID={`report-card-${reportType}`}
                                        sharedReport={getSharedReportRecord(reportType)}
                                    />
                                ))}
                        />
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ReportsList);
